import { httpsCallable, HttpsCallableResult } from "firebase/functions";
import { createContext, useContext, useEffect, useState, useRef } from "react";
import { useFirebase } from "./FirebaseContext";
import { onValue, ref } from "@firebase/database";
import { Category, EventTypes, GameEvent, GameState, Player, JeopardyStates } from "types";
import { useLocation, useNavigate } from "react-router-dom";
import { useGameSounds } from "../hooks/useGameSounds";
import { useElevenLabsTTS } from "../hooks/useElevenLabsTTS";
import api from "source-map-explorer";

export type GameContextType = {
  gameId: string | null;
  gameState?: GameState;
  currentPlayer?: Player;
  isLoadingGameState: boolean;
  isActivePlayer: boolean;
  isBuzzedIn: boolean;
  isHost: boolean;
  isHostOnly: boolean;
  activeCategory?: Category;
  sessionGames: { gameId: string; status: string; topic?: string; enableTTS?: boolean; ttsProgress?: { current: number; total: number; message?: string } }[];
  setGameId: (gameId: string) => void;
  setIsHost: (isHost: boolean) => void;
  setHostOnly: (isHostOnly: boolean) => void;
  createGame: (topic?: string, maxPlayers?: number | null) => Promise<string | null>;
  sendEvent: (event: GameEvent) => Promise<HttpsCallableResult<unknown> | void>;
  addPlayer: ({
    name,
    gameId,
    customTopic,
  }: {
    name: string;
    gameId: string;
    customTopic?: string;
  }) => Promise<void>;
  submitAnswer: (
    event: GameEvent
  ) => Promise<HttpsCallableResult<unknown> | void>;
  loadData: () => void;
  ping: () => ReturnType<typeof setTimeout>;
};

export const GameContext = createContext<GameContextType>({
  gameId: null,
  isLoadingGameState: true,
  isActivePlayer: false,
  isBuzzedIn: false,
  isHost: false,
  isHostOnly: false,
  sessionGames: [],
  setGameId: () => { },
  setIsHost: () => { },
  setHostOnly: () => { },
  createGame: async () => "",
  sendEvent: async () => { },
  addPlayer: async () => { },
  submitAnswer: async () => { },
  ping: () => setTimeout(() => { }, 0),
  loadData: () => { },
});

export type FunctionRequest = {
  ping?: boolean;
};

export type EventTransitionRequest = {
  gameId: string;
  event: GameEvent;
} & FunctionRequest;

export type AddPlayerRequest = {
  gameId: string;
  name: string;
  customTopic?: string;
} & FunctionRequest;

export type CreateGameRequest = {
  topic?: string;
  enableTTS?: boolean;
  maxPlayers?: number | null;
} & FunctionRequest;

export type PollPendingGamesRequest = {
  gameId?: string;
  gameIds?: string[];
} & FunctionRequest;

const getCurrentPlayer = (name: string | null, players?: Player[]) => {
  if (!players || !name) return;
  return players.find((player) => player.name === name);
};

const getIsActivePlayer = (
  currentPlayer?: Player,
  activePlayer?: Player | null,
  isHostOnly?: boolean
) => {
  // Host-only mode grants active player privileges
  if (isHostOnly) return true;
  if (!currentPlayer || !activePlayer) return false;
  return currentPlayer.name === activePlayer.name;
};

const getIsBuzzedIn = (
  currentPlayer?: Player,
  buzzedInPlayer?: Player | null
) => {
  if (!currentPlayer || !buzzedInPlayer) return false;
  return currentPlayer.name === buzzedInPlayer.name;
};

export const GameProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { pathname } = useLocation();
  const { playRightAnswer, playTimesUp } = useGameSounds();
  const previousStateRef = useRef<JeopardyStates | undefined>();
  const previousScoreRef = useRef<number>(0);
  // Extract game ID from various route patterns
  const getInitialGameId = (): string | null => {
    const pathParts = pathname.split("/").filter(Boolean);
    // Routes: /host/:gameId, /play/:gameId, /cast/:gameId, or legacy /:gameId
    if (
      pathParts.length >= 2 &&
      ["host", "play", "cast"].includes(pathParts[0])
    ) {
      return pathParts[1];
    }
    if (
      pathParts.length === 1 &&
      !["host", "play", "cast"].includes(pathParts[0])
    ) {
      return pathParts[0]; // Legacy route
    }
    return null;
  };
  const [gameId, setGameId] = useState<string | null>(getInitialGameId());
  const [sessionGames, setSessionGames] = useState<
    { gameId: string; status: string; topic?: string; ttsProgress?: { current: number; total: number; message?: string } }[]
  >([]);
  const [gameState, setGameState] = useState<GameState>();
  const [isLoadingGameState, setIsLoadingGameState] = useState(!!gameId);
  const { functions, db } = useFirebase();
  const _createGame = httpsCallable<CreateGameRequest, string>(
    functions,
    "createGame"
  );
  const _sendEvent = httpsCallable<EventTransitionRequest>(
    functions,
    "sendEvent"
  );
  const _submitAnswer = httpsCallable<EventTransitionRequest>(
    functions,
    "submitAnswer"
  );
  const _addPlayer = httpsCallable<AddPlayerRequest, Player>(
    functions,
    "addPlayer"
  );
  const _addGamesToFirestore = httpsCallable<FunctionRequest, void>(
    functions,
    "addGameTSVToFirestore"
  );
  const _pollPendingGames = httpsCallable<
    PollPendingGamesRequest,
    { checked: number; completed: number; stillPending: number; errors: number }
  >(functions, "pollPendingGames");
  const [currentPlayer, setCurrentPlayer] = useState(
    getCurrentPlayer(
      localStorage.getItem("currentPlayerName"),
      gameState?.players
    )
  );
  const [isHost, setIsHost] = useState(false);
  const [isHostOnly, setIsHostOnly] = useState(false);
  const navigate = useNavigate();
  const activeCategory = gameState?.categories.find(
    (category) => category.id === gameState.activeClue?.categoryId
  );


  // Keep functions warm while a game is active
  // Only starts warming AFTER the game state has loaded (not on initial page load)
  // Pings every 5 minutes to prevent cold starts during gameplay
  useEffect(() => {
    if (!gameId || !gameState) return;

    // Don't ping immediately on mount - the game state loading doesn't need functions
    // Start warming after a short delay, then keep warm every 5 minutes
    const initialWarm = setTimeout(() => {
      _ping();
    }, 2000); // 2 second delay to not compete with initial load

    const warmInterval = setInterval(() => {
      _ping();
    }, 5 * 60 * 1000); // 5 minutes

    return () => {
      clearTimeout(initialWarm);
      clearInterval(warmInterval);
    };
  }, [gameId, !!gameState]); // Only re-run when gameId changes or gameState becomes truthy

  const createGame = async (topic?: string, maxPlayers?: number | null) => {
    try {
      _ping();
      // Add timeout to prevent hanging if emulators aren't running
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => {
          reject(new Error("Request timed out. Make sure Firebase emulators are running (npm run dev) or check your connection."));
        }, 10000); // 10 second timeout
      });

      const createGamePromise = _createGame({ topic, maxPlayers }).catch((err) => {
        throw err;
      });

      const result = await Promise.race([
        createGamePromise,
        timeoutPromise,
      ]);

      const { data: createdGameId } = result;

      if (createdGameId) {
        // If topic is provided, status will be "generating" (OpenAI), otherwise "pending" (random game)
        const initialStatus = topic ? "generating" : "pending";
        setSessionGames((prev) => [
          ...prev,
          { gameId: createdGameId, status: initialStatus, topic },
        ]);
      }
      return createdGameId;
    } catch (error) {
      console.error("Error in createGame:", error);
      throw error;
    }
  };

  const sendEvent = async (event: GameEvent) => {
    if (!gameId) return;
    const res = await _sendEvent({ gameId, event });
    return res;
  };

  const submitAnswer = async (event: GameEvent) => {
    if (!gameId) return;
    const res = await _submitAnswer({ gameId, event });
    return res;
  };

  const loadData = async () => {
    await _addGamesToFirestore();
  };

  useEffect(() => {
    setCurrentPlayer(
      getCurrentPlayer(
        localStorage.getItem("currentPlayerName"),
        gameState?.players
      )
    );
  }, [gameState?.players]);

  // Play sound effects on Display/Cast views only (not on Player views)
  // Right answer sound when score increases, times up sound when wrong/timeout
  // Skip sounds on /play routes to avoid duplicate audio from player devices
  const isPlayerView = pathname.startsWith('/play');
  
  useEffect(() => {
    // Don't play sounds from Player view - sounds should only come from Display/Cast
    if (!gameState || isPlayerView) return;

    const currentState = gameState.currentState;
    const previousState = previousStateRef.current;
    const buzzedInPlayer = gameState.buzzedInPlayer;
    const activePlayer = gameState.activePlayer;
    // Track the buzzed-in player's score (the one who is answering)
    const buzzedInScore = buzzedInPlayer?.score || 0;
    const previousScore = previousScoreRef.current;
    const isDailyDouble = gameState.activeClue?.isDailyDouble;

    // Check if we transitioned from PlayerAnswering to CategorySelection
    if (
      previousState === JeopardyStates.PlayerAnswering &&
      currentState === JeopardyStates.CategorySelection
    ) {
      // Compare the new active player's score to previous buzzed-in player's score
      // After a correct answer, the answering player becomes the active player
      const newActiveScore = activePlayer?.score || 0;
      if (newActiveScore > previousScore) {
        // Score increased - correct answer
        playRightAnswer();
      } else if (!isDailyDouble) {
        // Score did not increase and not a daily double - wrong answer or time expired
        playTimesUp();
      }
    }

    // Check if we transitioned from WaitingForBuzz to CategorySelection (time ran out, nobody buzzed)
    if (
      previousState === JeopardyStates.WaitingForBuzz &&
      currentState === JeopardyStates.CategorySelection
    ) {
      playTimesUp();
    }

    previousStateRef.current = currentState;
    // Store the buzzed-in player's score while they're answering
    // This way we can compare it after the transition
    if (buzzedInPlayer) {
      previousScoreRef.current = buzzedInScore;
    }
  }, [
    gameState?.currentState,
    gameState?.buzzedInPlayer?.score,
    gameState?.activePlayer?.score,
    gameState?.activeClue?.isDailyDouble,
    playRightAnswer,
    playTimesUp,
    isPlayerView,
  ]);

  useEffect(() => {
    // Set loading state immediately when gameId changes to a new value
    if (gameId) {
      setIsLoadingGameState(true);
    }

    // Subscribe to ONLY this specific game, not all games
    const gameRef = ref(db, `games/${gameId}`);
    const unsubscribe = gameId
      ? onValue(
        gameRef,
        (snapshot) => {
          const gameData = snapshot.val();
          if (gameData) {
            try {
              const newData = JSON.parse(gameData);
              if (newData) {
                setGameState({
                  currentState: newData.value,
                  ...newData.context,
                });

                // Update sessionGames with progress if available
                if (newData.context?.ttsProgress) {
                  setSessionGames((prev) =>
                    prev.map((game) =>
                      game.gameId === gameId
                        ? { ...game, ttsProgress: newData.context.ttsProgress }
                        : game
                    )
                  );
                }
              }
            } catch (parseError) {
              console.error("Error parsing game data:", parseError);
            }
          }
          setIsLoadingGameState(false);
        },
        (error) => {
          console.error("Database error:", error);
          setIsLoadingGameState(false);
        }
      )
      : () => { };

    return () => unsubscribe();
  }, [gameId, db]);

  const addPlayer: GameContextType["addPlayer"] = async ({ name, gameId, customTopic }) => {


    const newPlayer = await _addPlayer({ gameId, name, customTopic });

    if (newPlayer) {
      localStorage.setItem("currentPlayerName", newPlayer.data.name);
      setCurrentPlayer(newPlayer.data);
      setGameId(gameId);
    }
    // TODO: game does not exist
    // TODO: duplicate player
  };
  const _ping = () => {
    _addPlayer({ gameId: "", name: "ping", ping: true });
    _createGame({ ping: true });
    _sendEvent({ gameId: "", event: { type: EventTypes.PING }, ping: true });
    _submitAnswer({ gameId: "", event: { type: EventTypes.PING }, ping: true });
  };

  const ping = () => {
    _ping();
    return setTimeout(() => {
      _ping();
    }, 50000);
  };

  const isActivePlayer = getIsActivePlayer(
    currentPlayer,
    gameState?.activePlayer,
    isHostOnly
  );
  const isBuzzedIn = getIsBuzzedIn(currentPlayer, gameState?.buzzedInPlayer);

  return (
    <GameContext.Provider
      value={{
        gameId,
        gameState,
        currentPlayer,
        isLoadingGameState,
        isActivePlayer,
        isBuzzedIn,
        isHost,
        isHostOnly,
        activeCategory,
        sessionGames,
        setGameId,
        setIsHost,
        setHostOnly: setIsHostOnly,
        createGame,
        sendEvent,
        addPlayer,
        submitAnswer,
        loadData,
        ping,
      }}
    >
      {children}
    </GameContext.Provider>
  );
};

export const useGame = () => useContext(GameContext);
